import React, { useState, useEffect, useMemo } from 'react';
import { loadPreferences } from './examples/preferences';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import MDBox from 'components/MDBox';
import SettingsIcon from '@mui/icons-material/Settings';
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';
import theme from 'media/theme';
import themeRTL from 'media/theme/theme-rtl';
import themeDark from 'media/theme-dark';
import themeDarkRTL from 'media/theme-dark/theme-rtl';
import routes from 'routes';
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from 'context';
import brandWhite from './media/images/logo-ct.png';
import brandDark from './media/images/logo-ct-dark.png';
import brandFX from './media/images/Bullsheet (2).png';
import { db, collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, addDoc, updateDoc, onSnapshot, writeBatch } from './firebaseConfig';
import { getUserID } from './firebaseConfig';
import { getAuth, signOut } from 'firebase/auth';
import ProtectedRoute from './ProtectedRoute';



function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [trades, setTrades] = useState([]);
  const [userID, setUserID] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCapital = 0;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [profileInfo, setProfileInfo] = useState(null);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [tradeAccounts, setTradeAccounts] = useState([]);
  const [selectedAccountID, setSelectedAccountID] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingTeamPermission, setPendingTeamPermission] = useState(null);
  const [team, setTeam] = useState(null); // New state for team data
  const [preferredAccountID, setPreferredAccountID] = useState(null);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false); // Added state
  const GIFTED_ACCOUNTS = [
    'BZqRPWxemaMLU4oFhGdnyuzapOh1',
    'tnfnwSzbqzRksTjo82bZAO0d4Ly2',
    '2iSq7W3AttOQoR6jPiAPUwvbyqF3',
    'ev3xyjENZ0fZfSLbz7KYpvQHb613',
    '4JkJV2u5InQeearkcPKfFkbC73s2',
  ];
  console.log("Sidenav: team prop:", team);
console.log("Sidenav: miniSidenav state:", miniSidenav);
console.log("Sidenav: profileInfo:", profileInfo);

  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [useLocation().pathname]);

    // Fetch team data when profileInfo.teamId is available
    useEffect(() => {
      let unsubscribeTeam;
      if (isProfileLoaded && profileInfo?.teamId) {
        console.log('App: Setting up team listener for teamId:', profileInfo.teamId);
        const teamDocRef = doc(db, 'teams', profileInfo.teamId);
        unsubscribeTeam = onSnapshot(
          teamDocRef,
          (teamDoc) => {
            if (teamDoc.exists()) {
              const teamData = { id: teamDoc.id, ...teamDoc.data() };
              setTeam(teamData);
              console.log('App: Real-time team data:', teamData);
            } else {
              setTeam(null);
              console.log('App: No team found for teamId:', profileInfo.teamId);
            }
          },
          (error) => {
            console.error('App: Error listening to team data:', error);
            setTeam(null);
          }
        );
      } else {
        setTeam(null);
        console.log('App: No teamId in profileInfo or profile not loaded');
      }
    
      return () => {
        if (unsubscribeTeam) {
          unsubscribeTeam();
          console.log('App: Unsubscribed from team listener');
        }
      };
    }, [profileInfo?.teamId, isProfileLoaded]);

  // Fetch user data, profile, and trade accounts
  useEffect(() => {
    const fetchUserDataAndSetupAccounts = async () => {
      try {
        const uid = await getUserID();
        setUserID(uid);
        setIsLoggedIn(!!uid);

        if (uid) {
          // Fetch profile data
          const userDocRef = doc(db, 'Users', uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            const profile = data.profileInfo || {};
            const newProfileInfo = {
              fullName: profile.fullName || '',
              profilePicURL: data.profilePicURL || '',
              role: profile.role || 'member',
              teamRole: profile.teamRole || 'member',
              teamId: profile.teamId || null,
              inviteCode: profile.inviteCode || '',
            };
            console.log('Initial profileInfo set:', newProfileInfo);
            setProfileInfo(newProfileInfo);
            setIsAffiliate(data.affiliate || false);
            localStorage.setItem('profileInfo', JSON.stringify(profile));
            setIsProfileLoaded(!!newProfileInfo.role); // Set only if role exists
          } else {
            console.log('User document does not exist');
          }

          // Fetch preferences
          try {
            const preferences = await loadPreferences(uid);
            const prefAccountID = preferences?.preferredAccountID || null;
            setPreferredAccountID(prefAccountID);
            console.log('Preferred account ID loaded:', prefAccountID);
          } catch (error) {
            console.error('Error loading preferences:', error);
            setPreferredAccountID(null);
          }

          // Set up default account
          const defaultAccountRef = doc(db, 'Users', uid, 'tradeAccounts', 'default');
          const defaultAccountSnap = await getDoc(defaultAccountRef);

          if (!defaultAccountSnap.exists()) {
            await setDoc(defaultAccountRef, {
              name: 'Default',
              isDefault: true,
              broker: null,
              initialCapital: 0,
              createdAt: new Date().toISOString(),
              syncEnabled: false,
              syncCredentials: null,
            });
            console.log('Created default trade account for user:', uid);
          }

          // Fetch trade accounts
          const accountsQuery = collection(db, 'Users', uid, 'tradeAccounts');
          const unsubscribeAccounts = onSnapshot(accountsQuery, (snapshot) => {
            const accounts = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setTradeAccounts(accounts);
            console.log('Trade accounts:', accounts);
          });

          // Migrate existing trades to Default account (one-time)
          const oldTradesRef = collection(db, 'Users', uid, 'trades');
          const oldTradesSnap = await getDocs(oldTradesRef);
          if (!oldTradesSnap.empty) {
            const defaultTradesRef = collection(db, 'Users', uid, 'tradeAccounts', 'default', 'trades');
            const existingDefaultTrades = await getDocs(defaultTradesRef);
            if (existingDefaultTrades.empty) {
              const tradesToMigrate = oldTradesSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              for (const trade of tradesToMigrate) {
                await setDoc(doc(defaultTradesRef, trade.id), trade);
              }
              console.log('Migrated trades to Default account:', tradesToMigrate.length);
            }
          }

          return () => unsubscribeAccounts();
        }
      } catch (error) {
        console.error('Error fetching user ID or setting up accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDataAndSetupAccounts();
  }, []);

  // Set selectedAccountID with delay if needed
  useEffect(() => {
    const setAccount = () => {
      if (tradeAccounts.length > 0 && (selectedAccountID === null || !tradeAccounts.some(acc => acc.id === selectedAccountID))) {
        let newSelectedAccountID = null;
        if (preferredAccountID && tradeAccounts.some(acc => acc.id === preferredAccountID)) {
          newSelectedAccountID = preferredAccountID;
          console.log('Set selectedAccountID from preferences:', preferredAccountID);
        } else {
          const defaultAccount = tradeAccounts.find(acc => acc.isDefault);
          newSelectedAccountID = defaultAccount ? defaultAccount.id : tradeAccounts[0].id;
          console.log('Set selectedAccountID from default or first:', newSelectedAccountID);
        }
        setSelectedAccountID(newSelectedAccountID);
      }
    };
    setAccount();
  }, [tradeAccounts, preferredAccountID, selectedAccountID]);

  // Fetch trades for the selected account
  useEffect(() => {
    if (userID && selectedAccountID) {
      const tradesQuery = query(collection(db, 'Users', userID, 'tradeAccounts', selectedAccountID, 'trades'));
      const unsubscribeTrades = onSnapshot(tradesQuery, (snapshot) => {
        const fetchedTrades = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrades(fetchedTrades);
        console.log('Trades fetched for account:', selectedAccountID, fetchedTrades);
      });

      return () => unsubscribeTrades();
    }
  }, [userID, selectedAccountID]);

  // Real-time profile updates
  useEffect(() => {
    let unsubscribe;
    if (userID) {
      const userDocRef = doc(db, 'Users', userID);
      unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log('Real-time profile data:', data);
          setIsAffiliate(data.affiliate === true);
          const profile = data.profileInfo || {};
          const newProfileInfo = {
            fullName: profile.fullName || '',
            profilePicURL: data.profilePicURL || '',
            role: profile.role || 'member',
            teamRole: profile.teamRole || 'member',
            teamId: profile.teamId || null,
            inviteCode: profile.inviteCode || '',
          };
          console.log('Real-time profileInfo set:', newProfileInfo);
          setProfileInfo(newProfileInfo);
          setIsProfileLoaded(!!newProfileInfo.role); // Set only if role exists
        } else {
          console.log('User document does not exist');
        }
      }, (error) => {
        console.error('Error fetching profile:', error);
      });
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userID]);

  // Fetch user data from WordPress API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch('/wp-json/fxbullsheet/v1/user-data');
        if (!response.ok) {
          throw new Error('Could not fetch user data');
        }
        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (userID) {
      fetchUserData();
    }
  }, [userID]);

  // Handle team permission modal
  useEffect(() => {
    const permissionData = sessionStorage.getItem('pendingTeamPermission');
    if (permissionData) {
      setPendingTeamPermission(JSON.parse(permissionData));
      setShowPermissionModal(true);
    }
  }, []);

  const handlePermissionSubmit = async (allow) => {
    if (pendingTeamPermission) {
      await updateDoc(doc(db, 'Users', pendingTeamPermission.userId), {
        [`profile.teamPermissions.${pendingTeamPermission.teamId}`]: allow,
      });
      sessionStorage.removeItem('pendingTeamPermission');
      setShowPermissionModal(false);
      setPendingTeamPermission(null);
    }
  };

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      console.log('User logged out successfully');
      localStorage.removeItem('userID');
      setUserID(null);
      setIsLoggedIn(false);
      window.location.reload();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const checkSubscriptionStatus = async (userId) => {
    console.log('Checking subscription status for user:', userId);
    console.log('Is gifted account?', GIFTED_ACCOUNTS.includes(userId));

    if (!userId || GIFTED_ACCOUNTS.includes(userId)) {
      console.log('Skipping check - no userId or gifted account');
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'Users', userId));
      const now = new Date();

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data:', userData.plan, 'isTrial:', userData.isTrial);

        if (userData.isTrial && userData.trialEndDate && new Date(userData.trialEndDate) <= now) {
          console.log('Trial period has ended, converting to demo account');
          await updateDoc(doc(db, 'Users', userId), {
            isDemo: true,
            isTrial: false,
            plan: 'demo_plan',
            demoUID: `DEMO_${userId}`,
            updatedAt: now.toISOString(),
          });

          const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
          if (subscriptionDoc.exists()) {
            await updateDoc(doc(db, 'subscriptions', userId), {
              status: 'expired',
              isDemo: true,
              isTrial: false,
              updatedAt: now.toISOString(),
            });
          }

          console.log('Trial conversion to demo complete');
          window.location.reload();
          return;
        }
      }

      const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));
      if (!subscriptionDoc.exists()) {
        console.log('No subscription found for user');
        return;
      }

      const subscription = subscriptionDoc.data();
      console.log('Current subscription status:', subscription.status);
      console.log('Convert to demo date:', subscription.convertToDemoAt);

      if (subscription.status === 'cancelled' && subscription.convertToDemoAt && new Date(subscription.convertToDemoAt) <= now) {
        console.log('Converting cancelled subscription to demo account');
        await updateDoc(doc(db, 'Users', userId), {
          isDemo: true,
          plan: 'demo_plan',
          demoUID: `DEMO_${userId}`,
          updatedAt: now.toISOString(),
        });

        await updateDoc(doc(db, 'subscriptions', userId), {
          status: 'expired',
          isDemo: true,
          updatedAt: now.toISOString(),
        });

        console.log('Conversion to demo complete');
        window.location.reload();
      }

      if (userDoc.exists() && GIFTED_ACCOUNTS.includes(userId)) {
        console.log('Updating gifted account status');
        await updateDoc(doc(db, 'Users', userId), {
          isGifted: true,
          updatedAt: now.toISOString(),
        });

        if (subscriptionDoc.exists()) {
          await updateDoc(doc(db, 'subscriptions', userId), {
            isGifted: true,
            updatedAt: now.toISOString(),
          });
        }
        console.log('Gifted status updated');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  useEffect(() => {
    let checkInterval;
    if (userID) {
      console.log('Starting subscription checks for user:', userID);
      checkSubscriptionStatus(userID);

      checkInterval = setInterval(() => {
        console.log('Running periodic subscription check');
        checkSubscriptionStatus(userID);
      }, 60 * 60 * 1000);
    }

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
        console.log('Cleared subscription check interval');
      }
    };
  }, [userID]);

  const addTrade = (trade) => {
    const newTrade = { ...trade, id: trades.length, userID: userID };
    setTrades([...trades, newTrade]);
  };

  const deleteTradeById = (tradeId) => {
    setTrades(trades.filter((trade) => trade.id !== tradeId));
  };

  const selectedAccount = tradeAccounts.find((acc) => acc.id === selectedAccountID) || { initialCapital: 0 };

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        if (!route.component) {
          return null;
        }
        return (
          <Route
            exact
            path={route.route}
            element={
              route.protected ? (
                <ProtectedRoute
                  element={route.component}
                  userID={userID}
                  userData={userData}
                  trades={trades}
                  initialCapital={selectedAccount.initialCapital}
                  setUserID={setUserID}
                  tradeAccounts={tradeAccounts}
                  selectedAccountID={selectedAccountID}
                  setSelectedAccountID={setSelectedAccountID}
                  tradeAccountID={selectedAccountID}
                  profileInfo={profileInfo}
                  allowedUserIds={route.allowedUserIds}
                  allowedRoles={route.allowedRoles}
                />
              ) : (
                <route.component setUserID={setUserID} />
              )
            }
            key={route.key}
          />
        );
      }
      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: 'pointer' }}
      onClick={handleConfiguratorOpen}
    >
      <SettingsIcon fontSize="medium" color="inherit" />
    </MDBox>
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <PayPalScriptProvider
      options={{
        'client-id': 'AbFdmfi1QIiAr9lzAurcJcJchIPpE6qzBg9hsOUaDu-JvzxtEF_LNbTWP8ji1rDk46OGx68iJaBBbvhH',
        currency: 'USD',
        intent: 'subscription',
        vault: true,
        components: 'buttons',
        'disable-funding': 'paylater,card',
      }}
    >
      {direction === 'rtl' ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <CssBaseline />
            {layout === 'dashboard' && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandFX : brandFX}
                  brandName="FX Bullsheet"
                  routes={routes({
                    userID,
                    userData,
                    trades,
                    initialCapital: selectedAccount.initialCapital,
                    setUserID,
                    isLoggedIn,
                    setIsLoggedIn,
                    handleLogout,
                    isAffiliate,
                    tradeAccounts,
                    selectedAccountID,
                    setSelectedAccountID,
                    profileInfo, // Add profileInfo
                  })}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                  profileInfo={profileInfo}
                  team={team} // Pass team to Sidenav
                />
                <Configurator />
                {configsButton}
              </>
            )}
            {layout === 'vr' && <Configurator />}
            <Dialog open={showPermissionModal} onClose={() => handlePermissionSubmit(false)}>
              <DialogTitle>Team Data Permission</DialogTitle>
              <DialogContent>
                <MDTypography>
                  Allow your coach to view your trade data for personalized guidance?
                </MDTypography>
              </DialogContent>
              <DialogActions>
                <MDButton onClick={() => handlePermissionSubmit(false)}>Deny</MDButton>
                <MDButton onClick={() => handlePermissionSubmit(true)} color="info">
                  Allow
                </MDButton>
              </DialogActions>
            </Dialog>
            <Routes>
              {getRoutes(
                routes({
                  userID,
                  userData,
                  trades,
                  initialCapital: selectedAccount.initialCapital,
                  setUserID,
                  isLoggedIn,
                  handleLogout,
                  isAffiliate,
                  tradeAccounts,
                  selectedAccountID,
                  setSelectedAccountID,
                  profileInfo, // Add profileInfo
                })
              )}
              <Route
                path="*"
                element={<Navigate to={userID ? '/fx-dashboard' : '/authentication/sign-in'} />}
              />
            </Routes>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          {layout === 'dashboard' && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandFX : brandFX}
                brandName="FX Bullsheet"
                routes={routes({
                  userID,
                  userData,
                  trades,
                  initialCapital: selectedAccount.initialCapital,
                  setUserID,
                  isLoggedIn,
                  handleLogout,
                  isAffiliate,
                  tradeAccounts,
                  selectedAccountID,
                  setSelectedAccountID,
                  profileInfo, // Add profileInfo
                })}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
                profileInfo={profileInfo}
                team={team} // Pass team to Sidenav
              />
              <Configurator />
              {configsButton}
            </>
          )}
          {layout === 'vr' && <Configurator />}
          <Dialog open={showPermissionModal} onClose={() => handlePermissionSubmit(false)}>
            <DialogTitle>Team Data Permission</DialogTitle>
            <DialogContent>
              <MDTypography>
                Allow your coach to view your trade data for personalized guidance?
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => handlePermissionSubmit(false)}>Deny</MDButton>
              <MDButton onClick={() => handlePermissionSubmit(true)} color="info">
                Allow
              </MDButton>
            </DialogActions>
          </Dialog>
          <Routes>
            {getRoutes(
              routes({
                userID,
                userData,
                trades,
                initialCapital: selectedAccount.initialCapital,
                setUserID,
                isLoggedIn,
                handleLogout,
                isAffiliate,
                tradeAccounts,
                selectedAccountID,
                setSelectedAccountID,
                profileInfo, // Add profileInfo
              })
            )}
            <Route
              path="*"
              element={<Navigate to={userID ? '/fx-dashboard' : '/authentication/sign-in'} />}
            />
          </Routes>
        </ThemeProvider>
      )}
    </PayPalScriptProvider>
  );
}

ProtectedRoute.propTypes = {
  element: PropTypes.elementType.isRequired,
};

export default App;